import React, { useState, useEffect } from 'react';
import { BookOpen, ChevronDown, Activity, Layers, Target } from 'lucide-react';
import { cn, logInteraction } from '../lib/utils';
import { TradingTerm } from '../constants';
import ErrorBoundary from './ErrorBoundary';

interface PageLegendProps {
  terms: TradingTerm[];
}

function PageLegendContent({ terms }: PageLegendProps) {
  const [isOpen, setIsOpen] = useState(false);

  // Guard against null/undefined terms and ensure it's an array
  const safeTerms = React.useMemo(() => {
    try {
      return Array.isArray(terms) ? terms.filter(t => t && typeof t === 'object' && t.term) : [];
    } catch (e) {
      console.error('[PageLegend] Error filtering terms:', e);
      return [];
    }
  }, [terms]);

  useEffect(() => {
    try {
      console.log(`[PageLegend] Lifecycle: ${isOpen ? 'OPENED' : 'CLOSED'} at ${new Date().toISOString()}`);
      logInteraction('PageLegend', isOpen ? 'Open' : 'Close');
    } catch (e) {
      // Ignore logging errors in headless
    }
  }, [isOpen]);

  useEffect(() => {
    console.log('[PageLegend] Component mounted in DOM');
    return () => console.log('[PageLegend] Component unmounting from DOM');
  }, []);

  return (
    <div className="fixed bottom-0 left-0 right-0 z-50 select-none pointer-events-none">
      <div 
        className={cn(
          "bg-[var(--app-text)] border-t border-[var(--card-border)] transition-all duration-500 ease-in-out overflow-hidden pointer-events-auto",
          isOpen ? "max-h-[400px] opacity-100 visible" : "max-h-0 opacity-0 invisible"
        )}
        aria-hidden={!isOpen}
      >
        <div data-testid="legend-panel-content" className="app-container py-6 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {safeTerms.length > 0 ? (
            safeTerms.map((item, idx) => (
              <div key={`${item.term}-${idx}`} className="space-y-1">
                <div className="flex items-center gap-2">
                  <span className="text-xs font-black text-emerald-500 uppercase tracking-widest italic">{item.term || 'Unknown'}</span>
                  <span className="text-[10px] px-1.5 py-0.5 rounded bg-[var(--card-bg)]/10 text-[var(--card-bg)]/60 font-bold uppercase">{item.category || 'General'}</span>
                </div>
                <p className="text-xs text-[var(--card-bg)]/60 leading-relaxed font-medium">{item.definition || 'No definition available.'}</p>
              </div>
            ))
          ) : (
            <div className="col-span-full text-center py-8">
              <p className="text-xs text-[var(--card-bg)]/40 font-bold uppercase tracking-widest italic">No specific terms defined for this module.</p>
            </div>
          )}
        </div>
      </div>
      <div className="bg-[var(--app-text)]/95 border-t border-[var(--card-border)] px-6 py-2 flex items-center justify-between shadow-2xl pointer-events-auto">
        <div className="flex items-center gap-6 text-[10px] font-bold text-[var(--card-bg)]/60 tracking-widest uppercase">
          <span className="flex items-center gap-1.5"><Activity size={12} className="text-emerald-600" /> System: Optimal</span>
          <span className="flex items-center gap-1.5"><Layers size={12} /> Latency: 4ms</span>
          <span className="flex items-center gap-1.5"><Target size={12} /> Execution: Smart Routing</span>
        </div>
        <button
          data-testid="legend-btn-toggle"
          onClick={() => {
            // Prevent rapid toggling that might crash headless browsers
            setIsOpen(prev => !prev);
          }}
          className="flex items-center gap-2 text-[10px] font-black text-[var(--card-bg)]/80 hover:text-emerald-500 transition-all uppercase tracking-widest group"
          aria-expanded={isOpen}
          type="button"
        >
          <BookOpen size={14} className="group-hover:scale-110 transition-transform" />
          {isOpen ? 'Close Module Legend' : 'Open Module Legend'}
          <ChevronDown size={14} className={cn("transition-transform duration-300", isOpen && "rotate-180")} />
        </button>
      </div>
    </div>
  );
}

const PageLegend = React.memo(function PageLegend(props: PageLegendProps) {
  return (
    <ErrorBoundary componentName="PageLegend">
      <PageLegendContent {...props} />
    </ErrorBoundary>
  );
});

export default PageLegend;
